import { IconButton, Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import MapIcon from '@material-ui/icons/MapOutlined'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import SatelliteIcon from '@material-ui/icons/Satellite'
import StraightenIcon from '@mui/icons-material/Straighten'
import { ParentSize } from '@visx/responsive'
import { upperFirst } from 'lodash'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import {
  DomainPermission_DomainEnum,
  DomainPermission_VerbEnum,
  GraphStackResponse,
  DroverEventsResponse,
} from 'src/api/gen'
import { exportData, formatExportDroverEvents, formatExportTripsLocationTypes } from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import useComponentControl from 'src/commons/hooks/useComponentControl'
import useSafeEffect from 'src/commons/hooks/useSafeEffect'
import { DrawLayer, MapActionBar, MapPopup } from 'src/commons/map'
import DroverPopup, { IClickedDroverPoint } from 'src/commons/map/DroverPopup'
import { addDroversToMap, addMapLayersToMap } from 'src/commons/map/drovers'
import MapBoxMap from 'src/commons/map/MapBoxMap'
import { clearMap } from 'src/commons/map/utils'
import { Can, userHasPermission } from 'src/commons/permissions'
import { IFilters, IGraphComponent } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import InformationBanner from 'src/components/InformationBanner'
import WarnBanner from 'src/components/WarnBanner'
import { useViewport } from 'src/layout/hooks'
import MeasurementPanel from 'src/pages/SupervisionMap/MeasurementPanel'
import SETTINGS from 'src/settings'

import { scrollToComponent } from '../hooks'

import { NumberOfTripsWithLocationSensors, NumberOfTripsWithLocationSensorsPercentage } from './charts'

function TripsWithSensorsComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(): Promise<GraphStackResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphNumberOfTripsWithSensors({ data })
  }

  function render(data: GraphStackResponse[], title?: string) {
    return <NumberOfTripsWithLocationSensors title={title} graphReport={data} granularity={props.filters.granularity} />
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportTripsLocationTypes(props.filters.granularity, false)
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={upperFirst(t('kpi.NumberOfTripsByLocationType.title'))}
      description={upperFirst(t('kpi.NumberOfTripsByLocationType.description'))}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
    />
  )
}

function TripsWithSensorsPercentageComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(): Promise<GraphStackResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphNumberOfTripsWithSensorsPercentage({ data })
  }

  function render(data: GraphStackResponse[], title?: string) {
    return (
      <NumberOfTripsWithLocationSensorsPercentage
        title={title}
        graphReport={data}
        granularity={props.filters.granularity}
      />
    )
  }

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportTripsLocationTypes(props.filters.granularity, true)
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={upperFirst(t('kpi.NumberOfTripsByLocationTypePercentage.title'))}
      description={upperFirst(t('kpi.NumberOfTripsByLocationTypePercentage.description'))}
      fetcher={fetcher}
      formatExportBuckets={formatExportBuckets}
      render={render}
    />
  )
}

export const useFilterAbnormalsIcon = function (include_abnormals: boolean) {
  const { t } = useTranslation()

  return include_abnormals
    ? [t('tooltips.exclude_abnormals.sensors'), RemoveCircleOutlineIcon]
    : [t('tooltips.include_abnormals.sensors'), AddCircleOutlineIcon]
}

interface ISensorsChartsProps {
  filters: IFilters
  onFilterChange: (key: keyof IFilters, newValue: IFilters[keyof IFilters]) => void
}

interface IDroverEventsMapboxProps {
  data: DroverEventsResponse
  filters: IFilters
  title?: string
}

function DroverEventsMapbox(props: IDroverEventsMapboxProps) {
  const { data, filters } = props

  const [map, setMap] = useState<mapboxgl.Map | undefined>(undefined)
  const [satellite, setSatellite] = useState<boolean>(false)
  const [clickedDroverPoint, setClickedDroverPoint] = useState<IClickedDroverPoint | undefined>(undefined)
  const [measure, setMeasure] = useState(false)

  const agencyViewport = useViewport()
  const classes = useStyles()
  const { t } = useTranslation()

  useEffect(() => {
    if (!map) {
      return
    }
    clearMap(map, ['drover-events-points'])
    clearMap(map, ['map-layers'], 'map-layers-source')
    addDroversToMap(map, data.drover_events, setClickedDroverPoint)
    addMapLayersToMap(map, data.polygons)
  }, [map, filters, data])

  const componentControl = useComponentControl(
    <>
      <MapActionBar>
        <IconButton onClick={() => setSatellite(!satellite)}>{satellite ? <MapIcon /> : <SatelliteIcon />}</IconButton>
      </MapActionBar>
      <MapActionBar fitContent>
        <IconButton onClick={() => setMeasure(!measure)}>
          <StraightenIcon color={measure ? 'primary' : 'inherit'} />
        </IconButton>
      </MapActionBar>
    </>,
    [satellite, measure]
  )

  if (data.too_large) {
    return (
      <InformationBanner
        showBanner={true}
        message={t('kpi.DroverEvents.too_large')}
        className={classes.informationBanner}
      />
    )
  }

  return (
    <div style={{ width: '100%' }}>
      <ParentSize>
        {({ width }) => (
          <MapBoxMap
            width={width}
            height={width / 2}
            onMapReady={(m) => setMap(m)}
            showSatellite={satellite}
            showScale="bottom-right"
            showSearchBar="top-left"
            viewport={agencyViewport}
            componentControl={componentControl}
          >
            {clickedDroverPoint && (
              <MapPopup coordinates={clickedDroverPoint.coordinates} verticalOffset={-20}>
                <DroverPopup droverEvent={clickedDroverPoint.drover} />
              </MapPopup>
            )}
            <DrawLayer active={measure} mode="draw_line_string">
              <MeasurementPanel onCancel={() => setMeasure(false)} />
            </DrawLayer>
          </MapBoxMap>
        )}
      </ParentSize>
    </div>
  )
}

function DroverEventsComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  async function fetcher(): Promise<DroverEventsResponse> {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphDroverEvents({ data })
  }

  function render(data: DroverEventsResponse, title?: string) {
    return <DroverEventsMapbox title={title} data={data} filters={props.filters} />
  }

  let customExport = undefined
  if (userHasPermission(['analyze', 'kpi'])) {
    customExport = (filename: string, data?: DroverEventsResponse) => {
      exportData(filename, data?.drover_events ?? [], formatExportDroverEvents(), true)
    }
  }

  return (
    <DashboardGraphWrapper
      updateOn={[props.filters]}
      title={upperFirst(t('kpi.DroverEvents.title'))}
      description={upperFirst(t('kpi.DroverEvents.description'))}
      fetcher={fetcher}
      customExport={customExport}
      render={render}
      defaultData={{
        polygons: [],
        drover_events: [],
        too_large: true,
      }}
    />
  )
}

function SensorsCharts(props: ISensorsChartsProps) {
  const classes = useStyles()

  const include_abnormals = !!props.filters.include_abnormals
  useSafeEffect(scrollToComponent)

  const [title, FilterAbnormalsIcon] = useFilterAbnormalsIcon(include_abnormals)

  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <Tooltip title={title} placement="bottom" classes={{ tooltip: classes.tooltip }}>
        <IconButton onClick={() => props.onFilterChange('include_abnormals', !include_abnormals)}>
          <FilterAbnormalsIcon className={classes.exportIcon} />
        </IconButton>
      </Tooltip>
      <Can I={DomainPermission_VerbEnum.read} an={DomainPermission_DomainEnum.trip}>
        <TripsWithSensorsComponent filters={props.filters} />
        <TripsWithSensorsPercentageComponent filters={props.filters} />
        {SETTINGS.app.enable_drover_map && <DroverEventsComponent filters={props.filters} />}
      </Can>
    </BaseChartWrapper>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  tooltip: {
    fontSize: 13,
  },
  exportIcon: {
    fontSize: 30,
    cursor: 'pointer',
    color: '#192233',
  },
  informationBanner: {
    margin: theme.spacing(1),
    marginBottom: theme.spacing(1), // to respect padding
  },
}))

export default SensorsCharts
